import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { likesService, messagesService, profileViewsService, profileService } from '../../../services/supabaseService';

const NotificationCenter = ({ onViewAll }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [matchChannel, setMatchChannel] = useState(null);
  const [messageChannel, setMessageChannel] = useState(null);
  const [viewChannel, setViewChannel] = useState(null);

  useEffect(() => {
    if (user) {
      loadInitialNotifications();
      setupRealtimeSubscriptions();
    }

    return () => {
      // Cleanup subscriptions
      if (matchChannel) likesService?.unsubscribeFromMatches(matchChannel);
      if (messageChannel) messagesService?.unsubscribeFromConversations(messageChannel);
      if (viewChannel) profileViewsService?.unsubscribeFromProfileViews(viewChannel);
    };
  }, [user]);

  const loadInitialNotifications = async () => {
    setIsLoading(true);
    try {
      const initialNotifications = [];

      // Load recent matches
      const matches = await likesService?.getMutualMatches(user?.id);
      const recentMatches = matches?.slice(0, 2) || [];
      recentMatches?.forEach(match => {
        const otherProfile = match?.likerId === user?.id ? match?.likedProfile : match?.likerProfile;
        initialNotifications?.push({
          id: `match-${match?.id}`,
          type: 'match',
          icon: 'Heart',
          title: 'New Match!',
          message: `You have a new match with ${otherProfile?.displayName || 'Anonymous User'}`,
          timestamp: getRelativeTime(new Date(match?.createdAt)),
          priority: 'high',
          unread: isRecent(match?.createdAt),
          createdAt: new Date(match?.createdAt)
        });
      });

      // Load recent profile views
      const viewers = await profileViewsService?.getRecentViewers(user?.id, 3);
      if (viewers?.length > 0) {
        const viewCount = viewers?.length;
        initialNotifications?.push({
          id: `views-${Date.now()}`,
          type: 'profile',
          icon: 'Eye',
          title: 'Profile Views',
          message: `${viewCount} user${viewCount > 1 ? 's' : ''} viewed your profile recently`,
          timestamp: getRelativeTime(new Date(viewers?.[0]?.viewedAt)),
          priority: 'low',
          unread: isRecent(viewers?.[0]?.viewedAt),
          createdAt: new Date(viewers?.[0]?.viewedAt)
        });
      }

      // Load unread messages count
      const unreadCount = await messagesService?.getUnreadCount(user?.id);
      if (unreadCount > 0) {
        initialNotifications?.push({
          id: `messages-${Date.now()}`,
          type: 'message',
          icon: 'MessageCircle',
          title: 'New Messages',
          message: `You have ${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`,
          timestamp: 'Just now',
          priority: 'medium',
          unread: true,
          createdAt: new Date()
        });
      }

      // Sort by creation time (newest first)
      initialNotifications?.sort((a, b) => b?.createdAt - a?.createdAt);
      setNotifications(initialNotifications?.slice(0, 4));
    } catch (error) {
      console.error('Load notifications error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to new matches
    const matchSub = likesService?.subscribeToNewMatches(user?.id, async (newMatch) => {
      try {
        // Fetch the other user's profile
        const otherUserId = newMatch?.likerId === user?.id ? newMatch?.likedId : newMatch?.likerId;
        const otherProfile = await profileService?.getProfile(otherUserId);
        
        const notification = {
          id: `match-${newMatch?.id}`,
          type: 'match',
          icon: 'Heart',
          title: 'New Match!',
          message: `You have a new match with ${otherProfile?.displayName || 'Anonymous User'}`,
          timestamp: 'Just now',
          priority: 'high',
          unread: true,
          createdAt: new Date()
        };
        
        setNotifications(prev => [notification, ...prev]?.slice(0, 4));
      } catch (error) {
        console.error('Handle new match error:', error);
      }
    });
    setMatchChannel(matchSub);

    // Subscribe to new conversations (new messages)
    const messageSub = messagesService?.subscribeToNewConversations(user?.id, async (newConversation) => {
      const notification = {
        id: `message-${newConversation?.id}`,
        type: 'message',
        icon: 'MessageCircle',
        title: 'New Message',
        message: 'You received a new message',
        timestamp: 'Just now',
        priority: 'medium',
        unread: true,
        createdAt: new Date()
      };
      
      setNotifications(prev => [notification, ...prev]?.slice(0, 4));
    });
    setMessageChannel(messageSub);

    // Subscribe to profile views
    const viewSub = profileViewsService?.subscribeToProfileViews(user?.id, async (newView) => {
      try {
        const viewer = await profileService?.getProfile(newView?.viewerId);
        
        const notification = {
          id: `view-${newView?.viewerId}-${Date.now()}`,
          type: 'profile',
          icon: 'Eye',
          title: 'Profile View',
          message: `${viewer?.displayName || 'Someone'} viewed your profile`,
          timestamp: 'Just now',
          priority: 'low',
          unread: true,
          createdAt: new Date()
        };
        
        setNotifications(prev => [notification, ...prev]?.slice(0, 4));
      } catch (error) {
        console.error('Handle profile view error:', error);
      }
    });
    setViewChannel(viewSub);
  };

  const getRelativeTime = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const isRecent = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / 3600000;
    return diffHours < 24; // Consider recent if within 24 hours
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const getPriorityBg = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10';
      case 'medium':
        return 'bg-warning/10';
      case 'low':
        return 'bg-success/10';
      default:
        return 'bg-muted';
    }
  };

  const unreadCount = notifications?.filter(n => n?.unread)?.length;

  if (isLoading) {
    return (
      <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-warm">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold mb-1">Notifications</h2>
          <p className="text-xs md:text-sm text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount > 1 ? 's' : ''}` : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <span className="px-2 py-1 bg-accent/10 text-accent rounded-full text-xs font-medium">
            {unreadCount} new
          </span>
        )}
      </div>
      <div className="space-y-2 md:space-y-3">
        {notifications?.length > 0 ? (
          notifications?.map((notification) => (
            <div
              key={notification?.id}
              className={`flex items-start gap-3 p-3 rounded-lg transition-smooth hover:bg-muted cursor-pointer ${
                notification?.unread ? 'bg-primary/5' : ''
              }`}
            >
              <div className={`flex-shrink-0 p-2 rounded-lg ${getPriorityBg(notification?.priority)}`}>
                <Icon
                  name={notification?.icon}
                  size={20}
                  color={`var(--color-${notification?.priority === 'high' ? 'error' : notification?.priority === 'medium' ? 'warning' : 'success'})`}
                />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <span className={`text-sm md:text-base font-medium ${
                    notification?.unread ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {notification?.title}
                  </span>
                  {notification?.unread && (
                    <span className="flex-shrink-0 ml-2 w-2 h-2 bg-accent rounded-full" />
                  )}
                </div>
                <p className="text-xs md:text-sm text-muted-foreground mb-1">
                  {notification?.message}
                </p>
                <span className="text-xs text-muted-foreground">{notification?.timestamp}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Bell" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No notifications yet</p>
          </div>
        )}
      </div>
      {notifications?.length > 0 && (
        <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
          <button
            onClick={onViewAll}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-muted rounded-lg font-caption font-medium text-sm transition-smooth hover:bg-muted/80"
          >
            View All Notifications
            <Icon name="ArrowRight" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;